"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  buildPolicyMemo,
  MEMO_TYPES,
  type MemoType,
  PAGE_CHOICES,
  type PageChoice,
  type PolicyMemoInput,
} from "@/lib/tool-kit/business-templates/policy-memo";

/** English strings. A translated content file overrides these through `ui`. */
export const POLICY_MEMO_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  typeLabel: "Kind of memo",
  types: {
    decision: "Decision memo — recommends an option",
    briefing: "Briefing memo — informs, no recommendation",
  },
  headerHeading: "The header",
  toLabel: "To",
  toPlaceholder: "e.g. Dr. Alicia Moreno, Director of Student Housing",
  fromLabel: "From",
  fromPlaceholder: "e.g. Sam Okafor, Policy Analyst",
  dateLabel: "Date",
  todayButton: "Use today's date",
  subjectLabel: "Subject (RE)",
  subjectPlaceholder: "e.g. Reducing waste in campus residence halls",
  lengthLabel: "Length limit",
  pageOptions: {
    "1": "1 page (500 words)",
    "2": "2 pages (1,000 words)",
    "3": "3 pages (1,500 words)",
    "5": "5 pages (2,500 words)",
  },
  contentHeading: "What the memo is about",
  issueLabels: {
    decision: "The problem, and why it needs a decision now",
    briefing: "The current situation",
  },
  issuePlaceholders: {
    decision:
      "Residence halls send most of their waste to landfill, and the contract with the hauler ends in June.",
    briefing: "What is happening now, with dates and figures.",
  },
  backgroundLabel: "Background the reader needs",
  backgroundPlaceholder:
    "Only the facts needed to follow the options: figures, dates, and what has been tried.",
  optionsLabel: "Options — one per line",
  optionsPlaceholder: "e.g. Keep the current contract",
  recommendationLabel: "Your recommendation (optional)",
  recommendationPlaceholder: "The option you recommend and the main reason.",
  fileName: "policy-memo",
  document: {
    heading: "Your policy memo",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part has your content.",
  },
  defaultTitle: "Policy Memo",
  titleWithSubject: "Memo: {subject}",
  subtitles: {
    decision: "Decision memo",
    briefing: "Briefing memo",
  },
  subtitlePages: "{pages} pages, about {words} words",
  headerLabels: {
    to: "TO",
    from: "FROM",
    date: "DATE",
    subject: "RE",
  },
  budgetHeading: "Word Budget",
  budgetNote:
    "A planning convention, not a standard, at 500 words a page single-spaced. Follow your instructor's or organization's format if it differs.",
  budget: {
    section: "Section",
    share: "Share",
    words: "Target words",
    total: "Total",
  },
  sectionNames: {
    summary: "Summary",
    problem: "The Problem",
    background: "Background",
    options: "Options",
    recommendation: "Recommendation",
    next: "Next Steps",
    situation: "Current Situation",
    implications: "Implications",
    watch: "What to Watch",
  },
  tips: {
    decision: {
      summary:
        "Lead with the recommended action and the reasons for it — the reader may stop here.",
      problem: "State what is wrong and why it needs a decision now.",
      background:
        "Give only the context needed to understand the options. This should not be the longest section.",
      options:
        "Offer two to four realistic options and the honest trade-offs of each, including the one you reject.",
      recommendation:
        "Pick one option, say why, and be specific enough to act on.",
      next: "Say who does what first, and by when.",
    },
    briefing: {
      summary:
        "State the bottom line first: what the reader most needs to know.",
      background: "Give the history the reader needs, no more.",
      situation: "Report what is happening now, with dates and figures.",
      implications: "Say what it means for the reader and the organization.",
      watch: "Say what to watch for, and when you will update the reader.",
    },
  },
  prompts: {
    summary:
      "In two or three sentences: the problem, the recommended action, and the main reason.",
    problem:
      "What is the problem, who is affected, and why does it need a decision now?",
    background:
      "What does the reader need to know to understand the options — facts, figures, and what has been tried?",
    options:
      "List two to four realistic options, including doing nothing if it is a real choice.",
    recommendation:
      "Which option do you recommend, and why does it beat the others?",
    next: "What happens first, who does it, and by when?",
    situation: "What is happening now? Give dates, figures, and sources.",
    implications:
      "What does this mean for the reader, and for the organization?",
    watch: "What should the reader watch for, and when will you update them?",
    briefingSummary:
      "In two or three sentences: the bottom line, and what the reader most needs to know or do.",
  },
  target: "Target: {words} words ({share}).",
  targetShare: "About {share} of the memo.",
  tableHead: {
    option: "Option",
    benefits: "Benefits",
    costs: "Costs and risks",
    affected: "Who is affected",
  },
  needTwo:
    "Add at least one more option: a decision memo needs something to choose between.",
  checklistHeading: "Before You Send",
  checklist: {
    decision: [
      "The first paragraph states the recommendation and the reasons",
      "The options are realistic and treated fairly",
      "Every figure has a source",
      "The recommendation says who should do what, and when",
      "A busy reader could stop after the summary and still act",
    ],
    briefing: [
      "The first paragraph states the bottom line",
      "Facts are separated from judgments",
      "Every figure has a source",
      "The memo says what changes and what to watch",
      "A busy reader could stop after the summary and still be informed",
    ],
  },
  example: {
    type: "decision",
    to: "Dr. Alicia Moreno, Director of Student Housing",
    from: "Sam Okafor, Policy Analyst",
    date: "16 October 2026",
    subject: "Reducing waste in campus residence halls",
    issue:
      "Residence halls send about 80% of their waste to landfill, and the hauling contract ends in June. A decision on the next contract is needed by February.",
    background:
      "Recycling bins were added to the halls in 2022, but contamination rates are high and the hauler has twice warned the university about them.",
    options:
      "Renew the current contract unchanged\nRenew with a contamination-reduction program\nAdd composting alongside recycling",
    recommendation: "",
    pages: "2",
  },
} as const;

const EMPTY: PolicyMemoInput = {
  type: "decision",
  to: "",
  from: "",
  date: "",
  subject: "",
  issue: "",
  background: "",
  options: "",
  recommendation: "",
  pages: "2",
};

type TextKey =
  | "to"
  | "from"
  | "date"
  | "subject"
  | "issue"
  | "background"
  | "options"
  | "recommendation";

export default function PolicyMemoGeneratorWidget() {
  const strings = useToolUi(POLICY_MEMO_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<PolicyMemoInput>(EMPTY);
  const doc = useMemo(
    () => buildPolicyMemo(input, strings, fmt),
    [input, strings, fmt]
  );
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const decision = input.type === "decision";

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput({ ...EMPTY, ...strings.example })}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.headerHeading}>
        <PillGroup<MemoType>
          label={strings.typeLabel}
          name="pm-type"
          onChange={(type) => setInput((previous) => ({ ...previous, type }))}
          options={MEMO_TYPES.map((value) => ({
            value,
            label: strings.types[value],
          }))}
          value={input.type}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="pm-to"
            label={strings.toLabel}
            onChange={update("to")}
            placeholder={strings.toPlaceholder}
            value={input.to}
          />
          <TextField
            id="pm-from"
            label={strings.fromLabel}
            onChange={update("from")}
            placeholder={strings.fromPlaceholder}
            value={input.from}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
          <TextField
            id="pm-date"
            label={strings.dateLabel}
            onChange={update("date")}
            value={input.date}
          />
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              update("date")(
                new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(
                  new Date()
                )
              )
            }
            type="button"
          >
            {strings.todayButton}
          </button>
        </div>
        <TextField
          id="pm-subject"
          label={strings.subjectLabel}
          onChange={update("subject")}
          placeholder={strings.subjectPlaceholder}
          value={input.subject}
        />
        <SelectField<PageChoice>
          id="pm-pages"
          label={strings.lengthLabel}
          onChange={(pages) => setInput((previous) => ({ ...previous, pages }))}
          options={PAGE_CHOICES.map((value) => ({
            value,
            label: strings.pageOptions[value],
          }))}
          value={input.pages}
        />
      </Panel>

      <Panel title={strings.contentHeading}>
        <TextAreaField
          id="pm-issue"
          label={strings.issueLabels[input.type]}
          onChange={update("issue")}
          placeholder={strings.issuePlaceholders[input.type]}
          prose
          rows={3}
          value={input.issue}
        />
        <TextAreaField
          id="pm-background"
          label={strings.backgroundLabel}
          onChange={update("background")}
          placeholder={strings.backgroundPlaceholder}
          prose
          rows={3}
          value={input.background}
        />
        {decision ? (
          <>
            <TextAreaField
              id="pm-options"
              label={strings.optionsLabel}
              onChange={update("options")}
              placeholder={strings.optionsPlaceholder}
              prose
              rows={4}
              value={input.options}
            />
            <TextAreaField
              id="pm-recommendation"
              label={strings.recommendationLabel}
              onChange={update("recommendation")}
              placeholder={strings.recommendationPlaceholder}
              prose
              rows={2}
              value={input.recommendation}
            />
          </>
        ) : null}
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="pm"
        labels={strings.document}
      />
    </div>
  );
}
